import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import {
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    ComposedChart,
    Scatter,
    Area,
    ReferenceLine,
} from "recharts";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { calculateYBands } from "@/lib/utils/sensitivity";
import { Badge, Skeleton } from "@heroui/react";
import useResizeObserver from "@/lib/utils/useResizeObserver";
import useRequest from "@/lib/request/useRequest";
import { roundTo } from "@/lib/utils/numbers";
import { COLOR_MAP } from "@/types/color";
import { Field } from "@/types/field";
import { FillLayerSpecification, SymbolLayerSpecification } from "mapbox-gl";
import { LineLayerSpecification } from "mapbox-gl";
import useMap from "@/lib/map/useMap";
import {
    addUpdateGeojsonLayers,
    addUpdateGeojsonSource,
    removeSourcesIfExists,
    removeLayersIfExists,
    moveLayersToTop,
} from "@/lib/utils/map";
import { Plot, TrendlineTable } from "@/types/plot";
import { FeatureCollection, MultiPolygon } from "geojson";
import { UseQueryResult } from "@tanstack/react-query";
import { scaleTime } from "d3-scale";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";
import useFeatureFlag from "@/lib/featureFlags/useFeatureFlags";
import { CategoricalChartFunc } from "recharts/types/chart/generateCategoricalChart";
import nTimeHorizontalImage from "@/assets/images/H_NTime_TM.png";
import {
    RangeValue,
    DateValue,
    Switch,
    // Popover,
    // PopoverTrigger,
    // PopoverContent,
    // Button,
} from "@heroui/react";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import { Imagery } from "@/types/imagery";
// import { ImageAnalytics } from "@/types/imagery";
// import SIBar from "@/components/field/insights/SIBar";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { faXmark } from "@fortawesome/free-solid-svg-icons";

const GRAPH_SOURCE = "trendline-graph-source";
const FILL_LAYER: FillLayerSpecification = {
    source: GRAPH_SOURCE,
    id: "trendline-graph-fill",
    type: "fill",
    filter: ["==", ["geometry-type"], "Polygon"],
    layout: {},
    paint: {
        "fill-color": ["get", "statusColor"],
        "fill-opacity": 0.8,
    },
};
const LINE_LAYER: LineLayerSpecification = {
    source: GRAPH_SOURCE,
    id: "trendline-graph-line",
    type: "line",
    layout: {},
    paint: {
        "line-color": ["get", "displayColor"],
        "line-width": 1,
    },
    minzoom: 14,
};
const SYMBOL_LAYER: SymbolLayerSpecification = {
    source: GRAPH_SOURCE,
    id: "trendline-graph-symbol",
    type: "symbol",
    layout: {
        "text-field": [
            "concat",
            ["case", ["==", ["get", "plot_type"], 1], "Can: ", "Ref: "],
            ["get", "pair_id"],
        ],
        "text-size": 12,
        "symbol-avoid-edges": true,
    },
};

type TrendlineLimit = {
    date: string;
    crop_stage_id: number;
    sufficient_threshold: number;
    deficient_threshold: number;
    insights_active: boolean;
};

function SITrendline({
    fieldId,
    senstiivity,
    disableResize,
    tableQuery,
    dateRange,
    selectedPairIds,
    disableMap,
    virtualSIMode,
}: {
    fieldId: string;
    senstiivity?: Field["insight_sensitivity"];
    disableResize?: boolean;
    tableQuery?: UseQueryResult<TrendlineTable[]>;
    dateRange: RangeValue<DateValue> | null;
    selectedPairIds: string[];
    disableMap?: boolean;
    virtualSIMode?: boolean;
}) {
    const { formatDateMMDDYYYY } = useI18n();
    const { t } = useTranslation("insights");
    const { useGetRequest } = useRequest();
    const { bulkSIEnabled, virtualSIEnabled } = useFeatureFlag();

    const [rangeOnTrendline, setRangeOnTrendline] = useState(true);
    const [showBulkSI, setShowBulkSI] = useState(false);
    const [showVirtualSI, setShowVirtualSI] = useState(virtualSIMode ?? false);

    const trendlineQuery = useGetRequest<(number | string)[][]>({
        url: `fields/${fieldId}/trendline/`,
        enabled: !!fieldId,
        params: {
            values: "si",
            groupby: "block",
            range: true,
        },
    });

    const bulkQuery = useGetRequest<
        {
            image_id: number;
            capture_date: string;
            mean_sensitized_si: number;
        }[]
    >({
        url: `fields/${fieldId}/bulk_trendline/`,
        enabled: !!fieldId && bulkSIEnabled,
    });

    const virtualSIQuery = useGetRequest<
        {
            imagery__capture_date__date: number;
            min_si: number;
            mean_si: number;
            max_si: number;
        }[]
    >({
        url: `fields/${fieldId}/virtual_trendline/`,
        enabled: !!fieldId,
    });
    const {
        filteredImagery,
        setSelectedImage,
        setSelectedDate,
        selectedCropCycle,
        selectedDate,
    } = useFieldDashboard() ?? {};

    const trendlineLimitsQuery = useGetRequest<TrendlineLimit[]>({
        url: `fields/${fieldId}/trendline_limits/`,
        enabled: !!fieldId,
        params: {
            crop_cycle_id: selectedCropCycle?.id,
        },
    });

    const uploadedPlots = useGetRequest<FeatureCollection<MultiPolygon, Plot>>({
        url: `fields/${fieldId}/uploaded_plots/`,
        params: { boundary: "analytic" },
    });
    const appliedPlots = useGetRequest<FeatureCollection<MultiPolygon, Plot>>({
        url: `fields/${fieldId}/applied_plots/`,
        params: { boundary: "analytic" },
    });
    const plots = useMemo(() => {
        return [
            ...(uploadedPlots?.data?.features ?? []),
            ...(appliedPlots?.data?.features ?? []),
        ];
    }, [appliedPlots?.data?.features, uploadedPlots?.data?.features]);

    const trendlineInCycle = useMemo(() => {
        if (!trendlineQuery.data) return [];

        const keys = trendlineQuery.data[0];

        return sortArrayByKey(
            trendlineQuery.data
                .slice(1)
                .map((row) => {
                    return row.reduce(
                        (obj, value, index) => {
                            if (value === null || value === 0) return obj;
                            const isCaptureDate = keys[index] == "capture_date";
                            obj[keys[index].toString()] = isCaptureDate
                                ? dayjs(value).toDate().getTime()
                                : roundTo((value as number) * 100);
                            return obj;
                        },
                        {} as {
                            capture_date: string;
                            [x: string | number]: number | string | Date | null;
                        },
                    );
                })
                .filter((row) => {
                    return (
                        dayjs(row.capture_date).isSameOrAfter(
                            dayjs(dateRange?.start?.toString()),
                        ) &&
                        dayjs(row.capture_date).isSameOrBefore(
                            dayjs(dateRange?.end?.toString()),
                        )
                    );
                })
                .map((row) => {
                    if (!selectedPairIds?.length) return row;
                    return Object.fromEntries(
                        Object.entries(row).filter(
                            ([key]) =>
                                key === "capture_date" ||
                                selectedPairIds?.includes(
                                    Number(key.split("_")[1]).toString(),
                                ),
                        ),
                    );
                }),
            "capture_date",
        );
    }, [dateRange, selectedPairIds, trendlineQuery.data]);

    const dataKeys = useMemo(() => {
        if (!trendlineInCycle?.length) return [];
        const pairIds = Array.from(
            new Set(
                trendlineInCycle
                    .map((row) => Object.keys(row))
                    .flat()
                    .map((key) => key.split("_")[1]),
            ),
        );
        return pairIds.slice(1).map((pair, index) => {
            return {
                pairId: pair,
                color: COLOR_MAP[index],
            };
        });
    }, [trendlineInCycle]);

    const [min, max] = useMemo(() => {
        const values = (trendlineInCycle
            .map((point) => Object.values(point).slice(1))
            .flat()
            .filter((val) => val !== null && val != 0) ?? []) as number[];
        const virtualSIValues =
            virtualSIQuery.data
                ?.filter(
                    (point) =>
                        dayjs(point.imagery__capture_date__date).isSameOrAfter(
                            dayjs(dateRange?.start?.toString()),
                        ) &&
                        dayjs(point.imagery__capture_date__date).isSameOrBefore(
                            dayjs(dateRange?.end?.toString()),
                        ) &&
                        [point.mean_si, point.min_si, point.max_si].every(
                            (val) => val !== null && val != 0,
                        ),
                )
                .flatMap((point) => [
                    point.min_si,
                    point.mean_si,
                    point.max_si,
                ]) ?? [];
        const allValues = [...values, ...virtualSIValues];
        return [
            Math.max(Math.floor(Math.min(...allValues) / 5) * 5, 0),
            Math.min(Math.ceil(Math.max(...allValues) / 5) * 5, 110),
        ];
    }, [
        dateRange?.end,
        dateRange?.start,
        trendlineInCycle,
        virtualSIQuery.data,
    ]);
    const bands = useMemo(
        () => calculateYBands(senstiivity ?? "moderate"),
        [senstiivity],
    );

    const { elementRef, isResizing } = useResizeObserver(250);

    const imageryByDate = useMemo(() => {
        if (!filteredImagery?.length) return {} as Record<string, Imagery>;
        return filteredImagery.reduce<Record<string, Imagery>>((acc, im) => {
            acc[dayjs(im.capture_date).format("YYYY-MM-DD")] = im;
            return acc;
        }, {});
    }, [filteredImagery]);

    // const [selectedImageForModal, setSelectedImageForModal] =
    //     useState<Imagery | null>(null);
    const chartClickInProgressRef = useRef(false);

    // const imageAnalyticsQueryForModal = useGetRequest<ImageAnalytics>({
    //     url: `imagery/${selectedImageForModal?.id}/analytics/`,
    //     enabled: !!selectedImageForModal?.id,
    // });

    const { map, mapLoaded } = useMap();

    useEffect(() => {
        if (!map || !mapLoaded || disableMap) return;

        addUpdateGeojsonSource(map, GRAPH_SOURCE, {
            type: "geojson",
            tolerance: 0,
            data: {
                type: "FeatureCollection",
                features: [],
            },
        });
        addUpdateGeojsonLayers(map, [FILL_LAYER, LINE_LAYER, SYMBOL_LAYER]);

        return () => {
            if (map) {
                removeLayersIfExists(map, [
                    FILL_LAYER.id,
                    LINE_LAYER.id,
                    SYMBOL_LAYER.id,
                ]);
                removeSourcesIfExists(map, [GRAPH_SOURCE]);
            }
        };
    }, [map, mapLoaded, disableMap]);

    const plotIdsByDate = useMemo(() => {
        return tableQuery?.data?.reduce(
            (acc, row) => {
                const captureDate = dayjs(row.capture_date).format(
                    "YYYY-MM-DD",
                );
                if (!acc[captureDate]) {
                    acc[captureDate] = [row.plot_id];
                }
                acc[captureDate].push(row.plot_id);
                return acc;
            },
            {} as Record<string, number[]>,
        );
    }, [tableQuery?.data]);

    const onEnter: CategoricalChartFunc = useCallback(
        (data) => {
            if (!map || !mapLoaded || disableMap) return;

            const activeDate = data.activePayload?.[0]?.payload?.capture_date;
            if (!activeDate) return;

            const plotIds =
                plotIdsByDate?.[dayjs(activeDate).format("YYYY-MM-DD")];
            if (!plotIds) return;
            const features =
                plots
                    ?.filter((plot) => plotIds?.includes(plot.id as number))
                    .map((plot) => {
                        const pairId = plot.properties.pair_id;
                        const displayColor = dataKeys?.find(
                            (d) => Number(d.pairId) === pairId,
                        )?.color;
                        const payload = data.activePayload?.[0]?.payload;
                        const statusColor = payload
                            ? bands?.find((band) => {
                                  return (
                                      payload[`mean_${pairId}`] >= band.y0 &&
                                      payload[`mean_${pairId}`] < band.y1
                                  );
                              })?.mapColor
                            : "gray";
                        return {
                            ...plot,
                            properties: {
                                ...plot.properties,
                                displayColor: displayColor || "gray",
                                statusColor: statusColor || "gray",
                            },
                        };
                    }) ?? [];

            addUpdateGeojsonSource(map, GRAPH_SOURCE, {
                type: "geojson",
                tolerance: 0,
                data: {
                    type: "FeatureCollection",
                    features,
                },
            });
            addUpdateGeojsonLayers(map, [SYMBOL_LAYER, FILL_LAYER, LINE_LAYER]);
            moveLayersToTop(map, [
                FILL_LAYER.id,
                LINE_LAYER.id,
                SYMBOL_LAYER.id,
            ]);
        },
        [map, mapLoaded, plotIdsByDate, plots, dataKeys, bands, disableMap],
    );

    const onLeave = useCallback(() => {
        if (!map || !mapLoaded) return;

        addUpdateGeojsonSource(map, GRAPH_SOURCE, {
            type: "geojson",
            tolerance: 0,
            data: {
                type: "FeatureCollection",
                features: [],
            },
        });
        removeLayersIfExists(map, [
            FILL_LAYER.id,
            LINE_LAYER.id,
            SYMBOL_LAYER.id,
        ]);
    }, [map, mapLoaded]);

    const onChartClick: CategoricalChartFunc = useCallback(
        (data) => {
            const captureDate = data.activePayload?.[0]?.payload?.capture_date;
            if (captureDate == null) return;
            const dateStr = dayjs(captureDate).format("YYYY-MM-DD");
            const image = imageryByDate[dateStr];
            if (image) {
                // setSelectedImageForModal(image);
                setSelectedImage?.(image ?? undefined);
                setSelectedDate?.(dateStr);
            }
        },
        [imageryByDate, setSelectedImage, setSelectedDate],
    );

    const maxValue = Math.max(
        ...trendlineInCycle.map((d) => d.capture_date as number),
    );
    const minValue = Math.min(
        ...trendlineInCycle.map((d) => d.capture_date as number),
    );
    const timeScale = scaleTime().domain([minValue, maxValue]);

    const domain = timeScale.domain().map((d) => d.getTime());
    const ticks = timeScale.ticks(5).map((d) => d.getTime());

    const operations = useGetRequest<{
        operations: { id: number; average_N: number; start_date: string }[];
    }>({
        url: `fields/${fieldId}/application_operations/`,
    });
    const operationsData = useMemo(() => {
        return (
            operations.data?.operations?.reduce(
                (acc, operation) => {
                    const startDate = dayjs(operation.start_date)
                        .toDate()
                        .getTime();
                    const totalN = roundTo(operation.average_N, 1);
                    if (!acc[startDate]) {
                        acc[startDate] = {
                            start_date: startDate,
                            total_N: totalN,
                        };
                    }
                    acc[startDate].total_N += totalN;
                    return acc;
                },
                {} as {
                    [x: string]: { start_date: number; total_N: number };
                },
            ) ?? {}
        );
    }, [operations.data?.operations]);

    const trendlineLimitsByDate = useMemo(() => {
        return trendlineLimitsQuery.data?.reduce<
            Record<string, TrendlineLimit>
        >((acc, point) => {
            acc[dayjs(point.date).format("YYYY-MM-DD")] = point;
            return acc;
        }, {});
    }, [trendlineLimitsQuery.data]);

    const chartData = useMemo(() => {
        const trendlineDates = trendlineInCycle.map((point) =>
            dayjs(point.capture_date as number)
                .toDate()
                .getTime(),
        );
        const operationDates =
            operations.data?.operations
                ?.filter(
                    (op) =>
                        dayjs(op.start_date).isSameOrAfter(
                            dayjs(dateRange?.start?.toString()),
                        ) &&
                        dayjs(op.start_date).isSameOrBefore(
                            dayjs(dateRange?.end?.toString()),
                        ),
                )
                ?.map((op) => dayjs(op.start_date).toDate().getTime()) ?? [];

        const virtualSIDates =
            virtualSIQuery.data
                ?.filter(
                    (point) =>
                        dayjs(point.imagery__capture_date__date).isSameOrAfter(
                            dayjs(dateRange?.start?.toString()),
                        ) &&
                        dayjs(point.imagery__capture_date__date).isSameOrBefore(
                            dayjs(dateRange?.end?.toString()),
                        ),
                )
                ?.map(
                    (point) =>
                        dayjs(point.imagery__capture_date__date)
                            .toDate()
                            .getTime() ?? [],
                ) ?? [];
        const allDatesSet = new Set([
            ...trendlineDates,
            ...operationDates,
            ...virtualSIDates,
        ]);

        const allDates = Array.from(allDatesSet).sort((a, b) => a - b);

        return allDates.map((date) => {
            const trendlinePoint = trendlineInCycle.find(
                (point) =>
                    dayjs(point.capture_date as number)
                        .toDate()
                        .getTime() === date,
            );
            const operation = operationsData?.[date];

            const bulk_si = bulkQuery.data?.find(
                (point) =>
                    dayjs(point.capture_date).toDate().getTime() === date,
            )?.mean_sensitized_si;

            const virtual_si = virtualSIQuery.data?.find(
                (point) =>
                    dayjs(point.imagery__capture_date__date)
                        .toDate()
                        .getTime() === date,
            );

            const limitOnDay =
                trendlineLimitsByDate?.[dayjs(date).format("YYYY-MM-DD")];
            const sufficient_threshold = limitOnDay?.sufficient_threshold;
            const deficient_threshold = limitOnDay?.deficient_threshold;

            const chartPoint = {
                ...(trendlinePoint ?? { capture_date: date }),
                total_N: operation ? min + 1 : null,
                bulk_si: bulk_si ? bulk_si * 100 : null,

                virtual_min_si: virtual_si?.min_si
                    ? roundTo(virtual_si?.min_si ?? null, 1)
                    : null,
                virtual_mean_si: virtual_si?.mean_si
                    ? roundTo(virtual_si?.mean_si ?? null, 1)
                    : null,
                virtual_max_si: virtual_si?.max_si
                    ? roundTo(virtual_si?.max_si ?? null, 1)
                    : null,
                virtual_range: [virtual_si?.min_si, virtual_si?.max_si],
                sufficient_threshold: [
                    sufficient_threshold ? sufficient_threshold * 100 : null,
                    100,
                ],
                saturated_threshold: [100, 1000],
                approaching_deficient_threshold: [
                    deficient_threshold ? deficient_threshold * 100 : null,
                    sufficient_threshold ? sufficient_threshold * 100 : null,
                ],
                deficient_threshold: [
                    0,
                    deficient_threshold ? deficient_threshold * 100 : null,
                ],
                outside_insights:
                    !limitOnDay || !limitOnDay.insights_active
                        ? [0, 1000]
                        : null,
            } as {
                capture_date: number;
                total_N: number | null;
                bulk_si: number | null;
                virtual_min_si: number | null;
                virtual_mean_si: number | null;
                virtual_max_si: number | null;
                virtual_range: number[] | null;
                sufficient_threshold: number[] | null;
                saturated_threshold: number[] | null;
                approaching_deficient_threshold: number[] | null;
                deficient_threshold: number[] | null;
                outside_insights: number[] | null;
                [key: string]:
                    | number
                    | number[]
                    | null
                    | TrendlineLimit
                    | boolean;
            };
            const pairIds = dataKeys?.map((d) => d.pairId);
            if (trendlinePoint) {
                pairIds?.forEach((pairId) => {
                    chartPoint[`range_${pairId}`] = [
                        trendlinePoint[`min_${pairId}`] as number,
                        trendlinePoint[`max_${pairId}`] as number,
                    ];
                });
            }
            return chartPoint;
        });
    }, [
        trendlineInCycle,
        operations.data?.operations,
        virtualSIQuery.data,
        dateRange?.start,
        dateRange?.end,
        operationsData,
        bulkQuery.data,
        min,
        trendlineLimitsByDate,
        dataKeys,
    ]);

    const operationsByDate = useMemo(() => {
        if (!operations.data?.operations) return {};
        return operations.data.operations.reduce(
            (acc, op) => {
                const date = dayjs(op.start_date).toDate().getTime();
                acc[date] = roundTo(op.average_N, 1);
                return acc;
            },
            {} as Record<number, number>,
        );
    }, [operations.data?.operations]);

    return (
        <>
            <div className="flex flex-row items-center gap-2 text-sm">
                {t("showPlotRanges", {
                    ns: "insights",
                })}
                <Switch
                    id="rangeOnTrendline"
                    size="sm"
                    isSelected={rangeOnTrendline}
                    onValueChange={setRangeOnTrendline}
                />
                {bulkSIEnabled && (
                    <>
                        {t("showBulkSI", {
                            ns: "insights",
                        })}
                        <Switch
                            id="showBulkSI"
                            size="sm"
                            isSelected={showBulkSI}
                            onValueChange={setShowBulkSI}
                        />
                    </>
                )}
                {virtualSIEnabled && (
                    <Badge
                        color="secondary"
                        variant="solid"
                        content="Beta"
                        placement="top-right"
                    >
                        <div className="px-4 pr-6">
                            {t("virtualSI", {
                                ns: "insights",
                            })}
                            <Switch
                                id="showVirtualSI"
                                size="sm"
                                isSelected={showVirtualSI}
                                onValueChange={setShowVirtualSI}
                                className="ml-2"
                            />
                        </div>
                    </Badge>
                )}
                <div className="ml-auto">
                    <img
                        src={nTimeHorizontalImage}
                        alt="N Time"
                        className="h-10 w-auto"
                    />
                </div>
            </div>
            {/* <Popover
                isOpen={!!selectedImageForModal}
                onOpenChange={(open) => {
                    if (!open) {
                        if (chartClickInProgressRef.current) {
                            chartClickInProgressRef.current = false;
                            return;
                        }
                        setSelectedImageForModal(null);
                    }
                }}
                placement="top"
            >
                <PopoverTrigger> */}
            <div
                className="w-full h-[28rem] outline-none cursor-pointer"
                ref={elementRef}
                onClickCapture={() => {
                    chartClickInProgressRef.current = true;
                }}
            >
                {isResizing && !disableResize && (
                    <Skeleton className="h-full w-full" />
                )}
                {(!isResizing || disableResize) && (
                    <>
                        {!chartData.length ? (
                            <div className="bg-gray-100 w-full h-[400px] rounded-md">
                                <div className=" p-3 italic text-center">
                                    {t("messages.noDataSelectedDates")}
                                </div>
                            </div>
                        ) : (
                            <ResponsiveContainer
                                width="100%"
                                height={400}
                                style={{ cursor: "pointer" }}
                            >
                                <ComposedChart
                                    data={chartData}
                                    margin={{ bottom: 16, left: 8, right: 4 }}
                                    onMouseMove={onEnter}
                                    onMouseLeave={onLeave}
                                    onClick={onChartClick}
                                    style={{ cursor: "pointer" }}
                                >
                                    <CartesianGrid />
                                    {selectedDate && (
                                        <ReferenceLine
                                            x={dayjs(selectedDate)
                                                .toDate()
                                                .getTime()}
                                            stroke="black"
                                            strokeOpacity={0.9}
                                            strokeWidth={1.5}
                                        />
                                    )}
                                    <XAxis
                                        dataKey="capture_date"
                                        spacing={50}
                                        dy={16}
                                        dx={-8}
                                        angle={-10}
                                        tickFormatter={(t) =>
                                            formatDateMMDDYYYY(t)
                                        }
                                        scale="linear"
                                        type="number"
                                        domain={domain}
                                        ticks={ticks}
                                    />
                                    <YAxis
                                        domain={[Number(min), Number(max)]}
                                        allowDataOverflow
                                        allowDecimals={false}
                                        tickCount={(max - min) / 5 + 1}
                                        tickFormatter={(t) => `${t}%`}
                                        tickSize={0}
                                        fontSize={15}
                                        label={{
                                            value: t("chartLabels.sufficiency"),
                                            angle: -90,
                                            position: "left",
                                            dx: 10,
                                        }}
                                    />
                                    <Tooltip
                                        cursor={{
                                            stroke: "#000",
                                            strokeWidth: 2,
                                        }}
                                        labelFormatter={(l) =>
                                            dayjs(l).format("ll")
                                        }
                                        formatter={(value, name, payload) => {
                                            if (name === "total_N") {
                                                const appOp =
                                                    operationsByDate[
                                                        payload.payload
                                                            .capture_date
                                                    ];
                                                const lbsN = appOp ?? "--";
                                                return [
                                                    `${lbsN} lbs N`,
                                                    t("tooltips.nApplied"),
                                                ];
                                            }
                                            if (name === "virtual_mean_si") {
                                                return [
                                                    `${value}%`,
                                                    t("virtualSI"),
                                                ];
                                            }
                                            if (
                                                name
                                                    ?.toString()
                                                    .includes("mean_")
                                            ) {
                                                const plotId = name
                                                    .toString()
                                                    .replace("mean_", "");
                                                return [
                                                    `${value}%`,
                                                    `${t("chartLabels.pairIdHash")}${plotId}`,
                                                ];
                                            }

                                            if (
                                                name
                                                    ?.toString()
                                                    .includes("range_")
                                            ) {
                                                return [undefined, undefined];
                                            }
                                        }}
                                    />

                                    {/* {bands?.map((band) => {
                                        return (
                                            <ReferenceArea
                                                key={band.color}
                                                y1={band.y0}
                                                y2={band.y1}
                                                fill={band.color}
                                                ifOverflow="hidden"
                                                fillOpacity={0.6}
                                            />
                                        );
                                    })} */}
                                    <Area
                                        dataKey="sufficient_threshold"
                                        fill="#ccffcc"
                                        stroke="none"
                                        dot={false}
                                        tooltipType="none"
                                    />
                                    <Area
                                        dataKey="saturated_threshold"
                                        fill="#ccccff"
                                        fillOpacity={0.3}
                                        stroke="none"
                                        dot={false}
                                        tooltipType="none"
                                    />
                                    <Area
                                        dataKey="deficient_threshold"
                                        fill="#ffcccc"
                                        fillOpacity={0.3}
                                        stroke="none"
                                        tooltipType="none"
                                    />
                                    <Area
                                        dataKey="approaching_deficient_threshold"
                                        fill="#ffffcc"
                                        fillOpacity={0.3}
                                        stroke="none"
                                        tooltipType="none"
                                    />

                                    {rangeOnTrendline &&
                                        dataKeys?.map((key) => {
                                            return (
                                                <Area
                                                    type="monotone"
                                                    key={`range_${key.pairId}`}
                                                    dataKey={`range_${key.pairId}`}
                                                    fillOpacity={0.3}
                                                    stroke="none"
                                                    fill={key.color}
                                                    connectNulls
                                                />
                                            );
                                        })}
                                    {dataKeys?.map((key) => {
                                        return (
                                            <Line
                                                type="monotone"
                                                key={`mean_${key.pairId}`}
                                                dataKey={`mean_${key.pairId}`}
                                                stroke={key.color}
                                                dot={{ fill: key.color }}
                                                strokeWidth={2}
                                                connectNulls
                                            />
                                        );
                                    })}
                                    <Scatter
                                        dataKey="total_N"
                                        fill="green"
                                        shape="triangle"
                                    />
                                    {bulkSIEnabled && showBulkSI && (
                                        <Line
                                            type="monotone"
                                            dataKey="bulk_si"
                                            stroke="black"
                                            connectNulls
                                            dot={false}
                                            strokeWidth={5}
                                        />
                                    )}
                                    {virtualSIEnabled && showVirtualSI && (
                                        <>
                                            <Area
                                                type="monotone"
                                                dataKey="virtual_range"
                                                connectNulls
                                                fill="red"
                                                stroke="none"
                                                dot={false}
                                                tooltipType="none"
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="virtual_mean_si"
                                                connectNulls
                                                stroke="red"
                                                dot={false}
                                                strokeWidth={3}
                                            />
                                        </>
                                    )}
                                </ComposedChart>
                            </ResponsiveContainer>
                        )}
                    </>
                )}
            </div>
            {/* </PopoverTrigger>
                <PopoverContent className="p-4 border-4 border-primary">
                    <div className="ml-auto">
                        <Button
                            isIconOnly
                            size="sm"
                            variant="light"
                            color="default"
                            onPress={() => setSelectedImageForModal(null)}
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </Button>
                    </div>
                    <div className="mb-2 font-semibold">
                        {selectedImageForModal
                            ? `SI by pair – ${formatDateMMDDYYYY(selectedImageForModal.capture_date)}`
                            : ""}
                    </div>
                    {selectedImageForModal && (
                        <SIBar
                            imageAnalyticsQuery={imageAnalyticsQueryForModal}
                            pairIds={selectedPairIds}
                        />
                    )}
                </PopoverContent>
            </Popover> */}
        </>
    );
}
export default SITrendline;
